import { useEffect, useRef, useState } from 'react';

// Stable default reference — an inline default object would be recreated on
// every render, retriggering the effect and rebuilding the observer each time.
const DEFAULT_OPTIONS = { rootMargin: '200px' };

// Returns [ref, hasBeenInView]. Once the element scrolls into view we latch
// the flag to true and disconnect the observer. This lets callers defer
// expensive work (like loading a Google Map) until the element is actually
// seen — avoiding billable Map Loads for tiles that are never viewed.
export default function useInView(options = DEFAULT_OPTIONS) {
  const ref = useRef(null);
  const [hasBeenInView, setHasBeenInView] = useState(false);

  // Depend on the primitive value, not the object reference, so passing an
  // inline options object from a caller can't thrash the observer either.
  const { rootMargin = '200px', threshold = 0 } = options;

  useEffect(() => {
    const node = ref.current;
    if (!node || hasBeenInView) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      // Fallback for environments without IntersectionObserver (e.g. tests).
      setHasBeenInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setHasBeenInView(true);
        observer.disconnect();
      }
    }, { rootMargin, threshold });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasBeenInView, rootMargin, threshold]);

  return [ref, hasBeenInView];
}
